import { useCallback } from "react";
import {
    calculateQuantity,
    setDependencies,
    findManufacturerProductId,
    findSubcategory,
    hasColorOptions,
    getMappedProductCategories,
    getDependentCategories,
    recalculateDependentQuantities,
} from "./utils/productHelpers";
import type { LegacyDependencyRule } from "../../../../types/productDependencies";

interface UseMaterialUpdateProps {
    materialSchema: any[];
    measurements: any;
    formulas: any;
    attributes: any[];
    dependencies: LegacyDependencyRule[];
    setSelectedValues: React.Dispatch<React.SetStateAction<Record<string, any>>>;
}

export function useMaterialUpdate({
    materialSchema,
    measurements,
    formulas,
    attributes,
    dependencies,
    setSelectedValues,
}: UseMaterialUpdateProps) {
    const apply = useCallback(
        (updater: (prev: Record<string, any>) => Record<string, any>) => {
            setSelectedValues((prev) => updater(prev));
        },
        [setSelectedValues],
    );

    const handleQuantityUpdate = useCallback(
        (category: string, direction: "add" | "remove") => {
            apply((prev) => {
                const next = { ...prev };
                const currentQty = next[category]?.quantity || 0;
                const newQty = direction === "add" ? currentQty + 1 : Math.max(0, currentQty - 1);
                next[category] = { ...next[category], quantity: newQty };

                recalculateDependentQuantities(
                    next,
                    category,
                    dependencies,
                    materialSchema,
                    measurements,
                    formulas,
                    attributes,
                );
                return next;
            });
        },
        [apply, dependencies, materialSchema, measurements, formulas, attributes],
    );

    const handleClearProduct = useCallback(
        (category: string) => {
            apply((prev) => {
                const next = { ...prev };
                const rule = dependencies.find((r) => r.Category === category);
                getMappedProductCategories(rule).forEach((dep) => delete next[dep]);
                getDependentCategories(dependencies, category).forEach((dep) => delete next[dep]);
                delete next[category];
                return next;
            });
        },
        [apply, dependencies],
    );

    const handleProductChange = useCallback(
        (product: any, category: string) => {
            if (!product) return;
            apply((prev) => {
                const next = { ...prev };
                const rule = dependencies.find((r) => r.Category === category);
                getMappedProductCategories(rule).forEach((dep) => delete next[dep]);

                const sub = findSubcategory(materialSchema, category, product);
                const manufacturerProductId = sub?.Id || findManufacturerProductId(materialSchema, category, product);

                next[category] = {
                    value: product,
                    systemLine: sub?.SystemLine,
                    quantity: 0,
                    manufacturerProductId,
                    awaitingColor: hasColorOptions(materialSchema, category, product),
                };

                next[category].quantity = calculateQuantity(
                    materialSchema,
                    measurements,
                    category,
                    product,
                    formulas,
                    attributes,
                    next
                );

                setDependencies(
                    dependencies,
                    next,
                    category,
                    product,
                    materialSchema,
                    measurements,
                    formulas,
                    attributes,
                    manufacturerProductId,
                );

                recalculateDependentQuantities(
                    next,
                    category,
                    dependencies,
                    materialSchema,
                    measurements,
                    formulas,
                    attributes,
                );
                return next;
            });
        },
        [materialSchema, measurements, formulas, attributes, dependencies, apply],
    );

    const handleColorChange = useCallback(
        (category: string, color: string | null, manufacturerId?: string) => {
            apply((prev) => ({
                ...prev,
                [category]: {
                    ...prev[category],
                    color: color || undefined,
                    manufacturerProductId: manufacturerId,
                    awaitingColor: false,
                },
            }));
        },
        [apply],
    );

    return { handleQuantityUpdate, handleClearProduct, handleProductChange, handleColorChange };
}
